import { useCallback, useEffect, useState } from "react";
import { ListChecks, CalendarPlus, Upload, Download, FileText } from "lucide-react";

import { runService } from "@/services/runService";
import { useCrmActions } from "@/context/CrmActionsContext";
import { TaskItem } from "@/components/run/TaskItem";
import { fmtDate } from "@/lib/datetime";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/common/StateBlocks";
import { toast } from "sonner";
import { NewTaskDialog } from "@/components/run/NewTaskDialog";

/**
 * Lightweight related Tasks + Documents for a Contact/Lead/Transaction.
 * Reuses the single task/document systems; does not duplicate them.
 */
export const RelatedRunSection = ({ entityType, entityId, related }) => {
  const { openNewTask, openNewEvent, openUploadDocument } = useCrmActions();
  const [tasks, setTasks] = useState([]);
  const [docs, setDocs] = useState([]);
  const [editTask, setEditTask] = useState(null);

  const load = useCallback(() => {
    if (!entityId) return;
    const params = { related_entity_type: entityType, related_entity_id: entityId };
    runService.listTasks({ ...params, view: "all" }).then((d) => setTasks(d.tasks)).catch(() => {});
    runService.listDocuments(params).then((d) => setDocs(d.documents)).catch(() => {});
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("crm:changed", h);
    return () => window.removeEventListener("crm:changed", h);
  }, [load]);

  const download = async (d) => {
    try {
      const blob = await runService.downloadDocument(d.id);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(e.message || "Could not download"); }
  };

  return (
    <div className="space-y-6" data-testid="related-run-section">
      {/* Tasks */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-bold text-foreground">Tasks</h3>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => openNewEvent(related)} data-testid="related-add-event"><CalendarPlus className="h-3.5 w-3.5" /> Event</Button>
            <Button size="sm" variant="outline" onClick={() => openNewTask(related)} data-testid="related-add-task"><ListChecks className="h-3.5 w-3.5" /> Task</Button>
          </div>
        </div>
        {tasks.length === 0 ? (
          <EmptyState title="No tasks yet" description="Add a task to keep the work on this record moving." />
        ) : (
          <div className="space-y-2" data-testid="related-tasks">
            {tasks.map((t) => <TaskItem key={t.id} task={t} onChanged={load} onEdit={setEditTask} />)}
          </div>
        )}
      </div>

      {/* Documents */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-bold text-foreground">Documents</h3>
          <Button size="sm" variant="outline" onClick={() => openUploadDocument(related)} data-testid="related-add-document"><Upload className="h-3.5 w-3.5" /> Upload</Button>
        </div>
        {docs.length === 0 ? (
          <EmptyState title="No documents yet" description="Upload contracts, disclosures and other files here." />
        ) : (
          <div className="space-y-2" data-testid="related-documents">
            {docs.map((d) => (
              <div key={d.id} className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-2.5" data-testid={`related-doc-${d.id}`}>
                <FileText className="h-4 w-4 shrink-0 text-accent" />
                <button onClick={() => download(d)} className="min-w-0 flex-1 truncate text-left text-sm font-medium text-foreground hover:text-accent">{d.file_name}</button>
                <span className="text-xs uppercase text-muted-foreground">{d.file_type}</span>
                <span className="hidden text-xs text-muted-foreground sm:inline">{fmtDate(d.created_at)}</span>
                <Button size="sm" variant="ghost" onClick={() => download(d)} data-testid={`related-doc-download-${d.id}`}><Download className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <NewTaskDialog open={!!editTask} onOpenChange={(v) => !v && setEditTask(null)} task={editTask} onCreated={() => { setEditTask(null); load(); }} />
    </div>
  );
};
